/**
 * Pure DTO ⇄ UI mappers for the delivery-rule REST API. Kept free of
 * React/query concerns so every branch is testable without mocking a
 * network call — see `deliveryRulesMappers.test.ts`.
 */
import type {
  Delivery,
  DeliveryFormat,
  DeliveryType,
  Platform,
  SendNowFormState,
  WhenToSend,
} from "../../components/organisms/DeliverStory/types";
import type {
  DeliveryChannelDto,
  DeliveryFormatDto,
  DeliveryRuleDto,
  DeliveryTargetDto,
  TriggerTypeDto,
} from "./deliveryRules.types";
import { mapDeliveryScheduleToScheduleConfig } from "./deliveryRulesApi";

/** Table shows at most this many destination chips; the rest collapse
 * into a "+N more" link (`hiddenDestinationCount`). */
const MAX_VISIBLE_DESTINATIONS = 3;

/** M1 read side: backend only persists `pdf`/`pptx` — `video`/`image-set`
 * can never come back from the wire, so they aren't part of this map. */
export const formatDtoToUi = (format: DeliveryFormatDto): DeliveryFormat =>
  format === "pptx" ? "presentation" : "pdf";

/** M2: `manual` rows are the side-effect rows Send Now creates; `subscription`
 * is never produced by this UI but reads as a schedule rather than crashing. */
export const triggerTypeToDeliveryType = (trigger: TriggerTypeDto): DeliveryType => {
  switch (trigger) {
    case "manual":
      return "immediate";
    case "alert":
      return "alert";
    case "scheduled":
    case "subscription":
    default:
      return "scheduled";
  }
};

/** M4: 1:1 today — `teams` has no backend channel, so it can never appear
 * in a DTO read back from the wire. */
export const channelToPlatform = (channel: DeliveryChannelDto): Platform => channel;

/** M3: prefer the stored label, then the live Slack channel name (from the
 * already-cached channel list), then fall back to the raw opaque ref so a
 * row never renders blank. */
export const resolveTargetLabel = (
  target: DeliveryTargetDto,
  channelNameById: Map<string, string>,
): string => target.display_label ?? channelNameById.get(target.target_ref) ?? target.target_ref;

export const mapRuleToDeliveryRow = (
  dto: DeliveryRuleDto,
  channelNameById: Map<string, string>,
): Delivery => {
  const labels = dto.targets.map((target) => resolveTargetLabel(target, channelNameById));
  const visible = labels.slice(0, MAX_VISIBLE_DESTINATIONS);
  const hidden = labels.length - visible.length;

  return {
    id: dto.id,
    platform: channelToPlatform(dto.channel),
    destinations: visible,
    ...(hidden > 0 ? { hiddenDestinationCount: hidden } : {}),
    deliveryType: triggerTypeToDeliveryType(dto.trigger_type),
    format: formatDtoToUi(dto.format),
    status: dto.status,
  };
};

export const mapRulesToDeliveryRows = (
  dtos: DeliveryRuleDto[],
  channelNameById: Map<string, string>,
): Delivery[] => dtos.map((dto) => mapRuleToDeliveryRow(dto, channelNameById));

/** Trigger type → the create/edit form's "when to send" tab. `subscription`
 * is never produced by this UI but reads as "schedule" rather than
 * crashing, same reasoning as `triggerTypeToDeliveryType`. */
export const triggerTypeToWhenToSend = (trigger: TriggerTypeDto): WhenToSend => {
  switch (trigger) {
    case "manual":
      return "send-now";
    case "alert":
      return "alert-on-condition";
    case "scheduled":
    case "subscription":
    default:
      return "schedule";
  }
};

/**
 * Maps a fetched rule back into the create/edit form's shape, for the
 * per-row Edit action. `fallback` (the form's current state) supplies
 * every field the wire shape can't restore:
 * - alert conditions: the backend has no wire contract for them yet, so
 *   an alert rule opens with `fallback`'s conditions, not the real ones.
 * - anything schedule-only when the rule isn't a schedule (`ends`, etc.).
 */
export const mapRuleDtoToFormState = (
  dto: DeliveryRuleDto,
  fallback: SendNowFormState,
): SendNowFormState => ({
  ...fallback,
  platform: channelToPlatform(dto.channel),
  destinations: dto.targets.map((target) => target.target_ref),
  whenToSend: triggerTypeToWhenToSend(dto.trigger_type),
  format: formatDtoToUi(dto.format),
  artifact: dto.content_config.artifact ?? fallback.artifact,
  theme: dto.content_config.theme ?? null,
  message: dto.content_config.text ?? fallback.message,
  schedule: dto.trigger_config.schedule
    ? mapDeliveryScheduleToScheduleConfig(dto.trigger_config.schedule, fallback.schedule)
    : fallback.schedule,
});
